"use client";

import React, { useState, useEffect } from "react";
import {
  FaHandshake,
  FaBuilding,
  FaGraduationCap,
  FaGlobe,
  FaCalendarAlt,
  FaArrowRight,
  FaMoneyBillWave,
  FaFileUpload,
} from "react-icons/fa";
import { MdOutlineTimer, MdCheckCircleOutline } from "react-icons/md";

export default function KerjasamaDashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total_kerjasama: 0,
    potensi_pendapatan: 0,
    mitra_aktif: 0,
    segera_berakhir: 0,
  });
  const [clusters, setClusters] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);

  useEffect(() => {
    // Mengambil data dari API Backend Laravel
    fetch(`${process.env.NEXT_PUBLIC_BASE_URl}/kerjasama`)
      .then((res) => res.json())
      .then((resData) => {
        if (resData.status === "success") {
          setSummary(resData.summary);
          setClusters(resData.clusters);
          setExpiringContracts(resData.expiring_contracts);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Gagal mengambil data:", err);
        setLoading(false);
      });
  }, []);

  const formatMiliar = (number) => {
    if (!number) return "Rp 0";
    const miliar = number / 1000000000;
    return `Rp ${miliar.toFixed(2)} M`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-lg font-bold text-slate-600">Memuat Data Kerjasama...</p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      id: 1,
      name: "Total Kerjasama",
      value: summary.total_kerjasama,
      icon: FaHandshake,
      color: "from-blue-500 to-indigo-600",
    },
    {
      id: 2,
      name: "Potensi Pendapatan",
      value: formatMiliar(summary.potensi_pendapatan),
      icon: FaMoneyBillWave,
      color: "from-emerald-500 to-teal-600",
    },
    {
      id: 3,
      name: "Mitra Aktif",
      value: summary.mitra_aktif,
      icon: MdCheckCircleOutline,
      color: "from-purple-500 to-pink-600",
    },
    {
      id: 4,
      name: "Segera Berakhir",
      value: summary.segera_berakhir,
      icon: MdOutlineTimer,
      color: "from-amber-500 to-orange-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/50 pt-[120px] pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-items md:flex-row md:justify-between md:items-center mb-10 gap-4">
          <div>
            <span className="text-sm font-bold text-blue-600 uppercase tracking-wider">
              Layanan Baru
            </span>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight mt-1">
              Dashboard Pemantauan Kerjasama
            </h1>
            <p className="text-slate-500 mt-2 text-lg">
              Visualisasi data dan monitoring status kerjasama Lingkup BPPP Tegal.
            </p>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.id}
                className="bg-white rounded-3xl p-6 shadow-[0_10px_40px_rgba(0,0,0,0.03)] border border-slate-100 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] transition-all duration-500 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-lg shadow-blue-500/10 group-hover:scale-110 transition-transform duration-500`}>
                    <Icon size={20} />
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Live Data
                  </span>
                </div>
                <h3 className="text-sm font-bold text-slate-500">
                  {stat.name}
                </h3>
                <p className="text-3xl font-black text-slate-800 mt-1">
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT COLUMN - CLUSTERS */}
          <div className="lg:col-span-2 bg-white rounded-3xl p-8 shadow-[0_10px_40px_rgba(0,0,0,0.03)] border border-slate-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-black text-slate-800">
                  Sebaran Kluster Mitra
                </h2>
                <p className="text-sm text-slate-500">
                  Berdasarkan kategori status mitra di database
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500">
                <FaGlobe size={18} />
              </div>
            </div>

            <div className="space-y-6">
              {clusters.map((cluster) => (
                <div key={cluster.name}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-slate-700">
                      {cluster.name}
                    </span>
                    <span className="text-sm font-black text-slate-900">
                      {cluster.count} <span className="text-slate-400 text-xs font-medium">({cluster.percentage}%)</span>
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${cluster.percentage}%`, backgroundColor: cluster.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN - EXPIRING CONTRACTS */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_rgba(0,0,0,0.03)] border border-slate-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-black text-slate-800">
                  Segera Berakhir
                </h2>
                <p className="text-sm text-slate-500">
                  Kontrak yang butuh perhatian
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500">
                <FaCalendarAlt size={16} />
              </div>
            </div>

            <div className="space-y-4">
              {expiringContracts.length > 0 ? (
                expiringContracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="p-4 rounded-2xl border border-slate-100 hover:border-blue-100 hover:bg-blue-50/30 transition-all duration-300 relative overflow-hidden group"
                  >
                    {contract.status === "critical" && (
                      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500" />
                    )}
                    {contract.status === "warning" && (
                      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-amber-500" />
                    )}
                    {contract.status === "safe" && (
                      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-500" />
                    )}

                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-sm font-bold text-slate-800 line-clamp-1 flex-1 pr-2">
                        {contract.partner}
                      </h4>
                      <span
                        className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                          contract.status === "critical"
                            ? "bg-red-50 text-red-600"
                            : contract.status === "warning"
                            ? "bg-amber-50 text-amber-600"
                            : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        {contract.daysLeft} Hari Lagi
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                        <FaCalendarAlt className="opacity-70" />
                        Hingga {contract.endDate}
                      </p>
                      <button className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1 text-xs font-bold">
                        Detail <FaArrowRight size={10} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-slate-500 text-sm py-4">
                  Tidak ada kontrak yang segera berakhir.
                </div>
              )}
            </div>

            <button className="w-full mt-6 py-3 rounded-2xl border-2 border-dashed border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 font-bold text-sm transition-colors duration-300">
              Lihat Semua Kontrak
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
